<style scoped>
.image-viewer {
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  user-select: none;
  position: relative;
}

.toolbar {
  height: 30px;
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: #434343;
}

.viewer {
  width: 100%;
  height: 460px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  position: relative;
}

.the-image {
  max-width: 100%;
  max-height: 100%;
  will-change: transform;
  display: block;
  pointer-events: none;
  user-select: none;
}

.viewer.grab {
  cursor: grab;
}

.viewer.grabbing {
  cursor: grabbing;
}

.btn-toolbar {
  background-color:transparent;
}
</style>

<template>
  <div class="image-viewer">
    <div
      ref="container"
      class="viewer"
      :class="{
        grab: scale > 1 && !dragging,
        grabbing: dragging,
      }"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
      @wheel.prevent="onWheel"
      @touchstart.passive="onTouchStart"
      @touchmove.passive="onTouchMove"
      @touchend.passive="onTouchEnd"
    >
      <img
        ref="img"
        :src="src"
        :alt="alt"
        class="the-image"
        :style="imageStyle"
        @load="onImageLoad"
        draggable="false"
      />
    </div>

    <div class="toolbar">
      <img
        @click="zoomOut"
        src="/assets/images/zoom-minus.png"
        alt="people in chat bubbles"
        width="16px"
        height="16px"
        style="cursor: pointer;"
        :style="{ opacity: scale <= minScale ? 0.4 : 1 }"
      >

      <img
        @click="zoomIn"
        src="/assets/images/zoom-plus.png"
        alt="people in chat bubbles"
        width="16px"
        height="16px"
        style="cursor: pointer;"
        :style="{ opacity: scale >= maxScale ? 0.4 : 1 }"
      >

      <img
        @click="maxZoom"
        src="/assets/images/zoom-full.png"
        alt="people in chat bubbles"
        width="16px"
        height="16px"
        style="cursor: pointer;"
        :style="{ opacity: scale >= maxScale ? 0.4 : 1 }"
      >

      <img
        @click="rotateLeft"
        src="/assets/images/rotate-left.png"
        alt="people in chat bubbles"
        width="16px"
        height="16px"
        style="cursor: pointer;"
      >

      <img
        @click="rotateRight"
        src="/assets/images/rotate-right.png"
        alt="people in chat bubbles"
        width="16px"
        height="16px"
        style="cursor: pointer;"
      >

      <img
        @click="reset"
        src="/assets/images/reset.png"
        alt="people in chat bubbles"
        width="19px"
        height="19px"
        style="cursor: pointer;"
      >

    </div>
  </div>
</template>

<script>

export default {
  name: "ImageViewer",
  props: {
    src: { type: String, required: true },
    alt: { type: String, default: "image" },
    minScale: { type: Number, default: 1 },
    maxScale: { type: Number, default: 15 }, // optional: give more room
    step: { type: Number, default: 0.25 }   // 👈 more zoom per click
  },
  data() {
    return {
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
      dragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      containerRect: null,
      imgNaturalWidth: 0,
      imgNaturalHeight: 0,
      touchState: {
        isPinching: false,
        startDist: 0,
        startScale: 1,
        lastTouchX: 0,
        lastTouchY: 0,
      },
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale}) rotate(${this.rotation}deg)`,
        transformOrigin: "center center",
        cursor: this.scale > 1 ? (this.dragging ? "grabbing" : "grab") : "default",
        userSelect: "none",
        touchAction: "none",
      };
    },
  },
  methods: {
    onImageLoad() {
      const img = this.$refs.img;
      this.imgNaturalWidth = img.naturalWidth || img.width;
      this.imgNaturalHeight = img.naturalHeight || img.height;
      this.updateContainerRect();
      this.clampTranslate();
    },
    updateContainerRect() {
      this.containerRect = this.$refs.container.getBoundingClientRect();
    },
    zoomIn() {
      if (this.scale >= this.maxScale) return;
      this.setScale(this.scale + this.step);
    },
    zoomOut() {
      if (this.scale <= this.minScale) return;
      this.setScale(this.scale - this.step);
    },
    reset() {
      this.scale = this.minScale;
      this.translateX = 0;
      this.translateY = 0;
      this.rotation = 0;
    },
    maxZoom() {
      if (this.scale >= this.maxScale) return;
      this.setScale(this.maxScale);
    },
    rotateLeft() {
      this.rotation -= 90;
    },
    rotateRight() {
      this.rotation += 90;
    },
    setScale(newScale) {
      const prevScale = this.scale;
      newScale = Math.min(this.maxScale, Math.max(this.minScale, Number(newScale.toFixed(4))));
      if (newScale === prevScale) return;
      this.scale = newScale;
      this.updateContainerRect();
      this.clampTranslate();
    },
    clampTranslate() {
      if (!this.containerRect || !this.imgNaturalWidth) return;
      const cw = this.containerRect.width;
      const ch = this.containerRect.height;
      const scaledW = this.imgNaturalWidth * this.scale;
      const scaledH = this.imgNaturalHeight * this.scale;
      const maxOffsetX = (scaledW - cw) / 2;
      const maxOffsetY = (scaledH - ch) / 2;
      const limitX = maxOffsetX > 0 ? maxOffsetX : cw / 2;
      const limitY = maxOffsetY > 0 ? maxOffsetY : ch / 2;
      this.translateX = Math.max(-limitX, Math.min(limitX, this.translateX));
      this.translateY = Math.max(-limitY, Math.min(limitY, this.translateY));
    },
    onMouseDown(e) {
      if (e.button !== 0) return;
      if (this.scale <= this.minScale) return; // ✅ allow grab at max zoom and any zoom > 1
      this.dragging = true;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
    },
    onMouseMove(e) {
      if (!this.dragging) return;
      const dx = e.clientX - this.lastMouseX;
      const dy = e.clientY - this.lastMouseY;
      this.lastMouseX = e.clientX;
      this.lastMouseY = e.clientY;
      this.translateX += dx;
      this.translateY += dy;
      this.clampTranslate();
    },
    onMouseUp() {
      if (!this.dragging) return;
      this.dragging = false;
    },
    onWheel(e) {
      const delta = -e.deltaY || e.wheelDelta;
      const dir = delta > 0 ? 1 : -1;
      const newScale = this.scale + dir * this.step;
      this.setScale(newScale);
    },
    onTouchStart(e) {
      this.updateContainerRect();
      if (e.touches.length === 1) {
        this.touchState.isPinching = false;
        const t = e.touches[0];
        this.touchState.lastTouchX = t.clientX;
        this.touchState.lastTouchY = t.clientY;
      } else if (e.touches.length === 2) {
        this.touchState.isPinching = true;
        const d = this.touchDistance(e.touches);
        this.touchState.startDist = d;
        this.touchState.startScale = this.scale;
      }
    },
    onTouchMove(e) {
      if (e.touches.length === 1 && !this.touchState.isPinching) {
        if (this.scale <= 1) return;
        const t = e.touches[0];
        const dx = t.clientX - this.touchState.lastTouchX;
        const dy = t.clientY - this.touchState.lastTouchY;
        this.touchState.lastTouchX = t.clientX;
        this.touchState.lastTouchY = t.clientY;
        this.translateX += dx;
        this.translateY += dy;
        this.clampTranslate();
      } else if (e.touches.length === 2) {
        const d = this.touchDistance(e.touches);
        if (!this.touchState.isPinching) {
          this.touchState.isPinching = true;
          this.touchState.startDist = d;
          this.touchState.startScale = this.scale;
          return;
        }
        const ratio = d / this.touchState.startDist;
        this.setScale(this.touchState.startScale * ratio);
      }
    },
    onTouchEnd(e) {
      if (e.touches.length < 2) {
        this.touchState.isPinching = false;
      }
    },
    touchDistance(touches) {
      const a = touches[0];
      const b = touches[1];
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    },
  },
  mounted() {
    window.addEventListener("resize", this.updateContainerRect);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateContainerRect);
  },
};
</script>


